/**
 * PromptBar — free-text need + a row of criteria indicators (Role, Location,
 * Années d'expérience, Skills, Industry). The indicators are NOT inputs: each
 * circle turns green as the agent's analysis covers that dimension. Cmd/Ctrl+
 * Enter submits. Brand purple send button, no emojis.
 */
import React, { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Check } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';

export type ChipKey =
  | 'job_title'
  | 'location'
  | 'years_of_experience'
  | 'skills'
  | 'industry';

const CHIP_KEYS: ChipKey[] = [
  'job_title',
  'location',
  'years_of_experience',
  'skills',
  'industry',
];

interface PromptBarProps {
  disabled?: boolean;
  /** Which criteria the agent has covered so far — drives the green state. */
  coverage: Partial<Record<ChipKey, boolean>>;
  onSubmit: (query: string) => void;
}

export const PromptBar: React.FC<PromptBarProps> = ({ disabled, coverage, onSubmit }) => {
  const { t } = useTranslation('agents');
  const [query, setQuery] = useState('');

  const canSubmit = !disabled && query.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(query.trim());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="rounded-2xl border border-light-lila bg-white p-3">
      <div className="flex items-start gap-2">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          rows={3}
          placeholder={t('sourcing.prompt_placeholder')}
          disabled={disabled}
          className="flex-1 resize-none bg-transparent text-sm text-dark-invirtus placeholder:text-dark-invirtus/40 outline-none"
        />
        <Button
          type="icon"
          intent="primary"
          size="sm"
          icon={<ArrowUp />}
          onClick={submit}
          disabled={!canSubmit}
          aria-label={t('sourcing.run')}
          className="shrink-0"
        />
      </div>

      {/* Criteria coverage indicators (read-only) */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        {CHIP_KEYS.map((key) => {
          const done = !!coverage[key];
          return (
            <span key={key} className="inline-flex items-center gap-1.5 text-xs text-dark-invirtus/80">
              <span
                className={[
                  'inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors',
                  done ? 'border-green bg-green text-white' : 'border-light-lila bg-white',
                ].join(' ')}
              >
                {done && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
              </span>
              {t(`sourcing.chips.${key}` as never)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

PromptBar.displayName = 'PromptBar';
